import { createContext, ReactNode, useContext, useState } from "react";
import { CreditCardInvoiceDateProps, CreditCardInvoiceProps, GeneratePaymentRequest, Pageable } from "@/models";
import { filterAndPageableToRequest, formatCurrencyToApi,showToast, sortResponseData } from "@/utils";
import { Api_Banking } from "@/services/Api/bankingApiConfig";
import { getAPIN8N } from "@/services/Api/n8nApiConfig";

interface CreditCardProps {
  children: ReactNode;
}

type Invoice = {
  yearMonth: string;
  status: "OVERDUE" | "CLOSED" | "OPEN" | "PAID";
  totalAmount: number;
  endDate: string;
  dueDate: string;
};

interface CreditCardContextData {
  creditCards: any[];
  createCreditCard: (data: any) => Promise<any>;
  creditCardData: any;
  getCreditCards: (filter: Pageable | {}, pageable: Pageable | {}) => any;
  getInitialCreditCardData: (cardId: string) => any;
  getInitialData: () => any;
  loading: boolean;
  setLoading: (value: boolean) => void;
  creditCardLoading: boolean;
  setCreditCardLoading: (value: boolean) => void;
  selectedCreditCardName: string;
  setSelectedCreditCardName: (value: string) => void;
  creditCardTransactionsData: Array<any>;
  initialDataIsLoading: boolean;
  creditCardLimit: any;
  updateCardStatus: (status: string, cardId: string) => void;
  getTotalValueTransactions: (array: Array<any>) => number;
  setCreditCardData: (value: any) => void;
  deleteCreditCard: (cardId: string) => Promise<any>;
  renameCreditCard: (data: any) => Promise<any>;
  changeCreditCardLimit: (data: any) => Promise<any>;
  editedLimits: any[];
  originalLimits: any[];
  setEditedLimits: (value: any) => void;
  downloadCreditCardStatement: (body: Object) => Promise<any>;
  getInitialCreditCardInvoicesListData: () => Promise<any>;
  patchDueDateCreditCardInvoice: (dueDay: number) => Promise<any>;
  getInitialCreditCardInvoicesData: (month: string) => Promise<any>;
  creditCardsInvoices: any[];
  creditCardInvoice: CreditCardInvoiceProps;
  getInvoicesReady: () => Promise<any>;
  getInvoicesPendingPayment: () => Promise<any>;
  postGeneratePayment: (data: GeneratePaymentRequest) => Promise<any>;
  currentCreditCardInvoiceDate: CreditCardInvoiceDateProps;
}

const CreditCardContext = createContext<CreditCardContextData>(
  {} as CreditCardContextData
);

const CreditCardProvider = ({ children }: CreditCardProps) => {
  const [initialDataIsLoading, setInitialDataIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creditCardLoading, setCreditCardLoading] = useState(false);
  const [creditCards, setCreditCards] = useState([]);
  const [creditCardData, setCreditCardData] = useState({} as any);
  const [creditCardTransactionsData, setCreditCardTransactionData] = useState<any>([]);
  const [creditCardLimit, setCreditCardLimit] = useState({} as any);
  const [selectedCreditCardName, setSelectedCreditCardName] = useState("");
  const [creditCardsInvoices, setCreditCardsInvoices] = useState<any>([]);
  const [creditCardInvoice, setCreditCardInvoice] = useState<CreditCardInvoiceProps>({} as CreditCardInvoiceProps);
  const [currentCreditCardInvoiceDate, setCurrentCreditCardInvoiceDate] = useState<CreditCardInvoiceDateProps>({} as CreditCardInvoiceDateProps);
  const baseUrl = "cards";
  const transactionsBaseUrl = "transactions"
  const limitsBaseUrl = "limits"
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const [editedLimits, setEditedLimits] = useState([]);
  const [originalLimits, setOriginalLimits] = useState([]);
  const Api_N8N = getAPIN8N();
  const changeCreditCardLimit = (data: any) => {
    return Api_Banking.patch(`${limitsBaseUrl}/change-limit`, data);
  }

  const createCreditCard = async (data) => {
    setLoading(true);
    try {
      const formattedData = {
        type: data.limitType,
        limitValue: formatCurrencyToApi(data?.newLimit),
        cardName: data.cardName,
      }
      await Api_Banking.post(baseUrl, formattedData);
      showToast({
        type: "success",
        message: "Cartao de credito criado com sucesso.",
      });
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setLoading(false);
    }
  };

  const getCreditCards = (
    filter?: Pageable | {},
    pageable?: Pageable | {}
  ) => {
    return Api_Banking.get<any>(
      `${baseUrl}${filterAndPageableToRequest({ filter, pageable })}`
    );
  };

  const getCreditCardId = (
    cardId: string,
  ) => {
    return Api_Banking.get<any>(
      `${baseUrl}/${cardId}`
    );
  };

  const getCreditCardTransactions = (cardId: string) => {
    return Api_Banking.get<any[]>(
      `${transactionsBaseUrl}?cardId=${cardId}`
    )
  }

  const getCreditCardLimit = () => {
    return Api_Banking.get<any>(`${limitsBaseUrl}`, {
      params: {
        idCard: null
      }
    })
  }

  const updateCardStatus = (status: "ACTIVE" | "BLOCKED", cardId: string) => {
    return Api_Banking.patch(`${baseUrl}`, {
      status,
      idCard: cardId,
      idSeller: user?.idSeller,
    });
  }
  const getTotalValueTransactions = (array) => {
    return array.reduce((soma, item) => {
        if (item.approved === true) {
            return soma + item.billingAmount;
        }
        return soma;
    }, 0);
  }

  const deleteCreditCard = (cardId: string) => {
    return Api_Banking.delete(`${baseUrl}`, {
      data: {
        idCard: cardId,
      }
    });
  }

  const renameCreditCard = (data: any) => {
    return Api_Banking.patch(`${baseUrl}/rename-card`, data);
  }

  const downloadCreditCardStatement = (body?: Object) => {
    return Api_N8N.post("v1/banking/card/transactions" , body)
  }

  const getCreditCardInvoicesList = (month?: string): any => {
    if(month) return Api_Banking.get(`${baseUrl}/invoices/${month}`)
    return Api_Banking.get(`${baseUrl}/invoices`)
  }

  const patchDueDateCreditCardInvoice = (dueDay: number) => {
    return Api_Banking.patch(`${baseUrl}/invoices/update-due-day/${dueDay}`)
  }

  const getInvoicesReady = () => {
    return Api_Banking.get(`${baseUrl}/invoices/ready-for-payment`)
  }

  const getInvoicesPendingPayment = () => {
    return Api_Banking.get(`${baseUrl}/invoices/verify-pending-payment`)
  }

  const postGeneratePayment = (data: GeneratePaymentRequest) => {
    return Api_Banking.post(`${baseUrl}/invoices/generate-payment`, data)
  }

  function getPriorityInvoiceDates(invoices: Invoice[]) {
    const parseDate = (d: string) => new Date(d);
  
    const overdueInvoices = invoices.filter(inv => inv.status === "OVERDUE");
    if (overdueInvoices.length > 0) {
      const oldestOverdue = overdueInvoices.reduce((prev, curr) =>
        parseDate(curr.dueDate) < parseDate(prev.dueDate) ? curr : prev
      );
      return { dueDate: oldestOverdue.dueDate, endDate: oldestOverdue.endDate, status: "OVERDUE" };
    }

    const closedInvoices = invoices.filter(inv => inv.status === "CLOSED");
    if (closedInvoices.length > 0) {
      const latestClosed = closedInvoices.reduce((prev, curr) =>
        parseDate(curr.endDate) > parseDate(prev.endDate) ? curr : prev
      );
      return { dueDate: latestClosed.dueDate, endDate: latestClosed.endDate, status: "CLOSED" };
    }

    const openInvoices = invoices.filter(inv => inv.status === "OPEN");
    if (openInvoices.length > 0) {
      const latestOpen = openInvoices.reduce((prev, curr) =>
        parseDate(curr.endDate) > parseDate(prev.endDate) ? curr : prev
      );
      return { dueDate: latestOpen.dueDate, endDate: latestOpen.endDate, status: "OPEN" };
    }

    return { dueDate: null, endDate: null, status: "OPEN"};
  }
  
  async function getInitialData() {
    try {
      setInitialDataIsLoading(true);
      const response = await getCreditCards({}, {});
      const sortedResponse = sortResponseData(response?.data, "cardName");
      setCreditCards(sortedResponse);
      const response_limit = await getCreditCardLimit();
      setCreditCardLimit(response_limit?.data);
      setEditedLimits(sortedResponse?.map((card: any) => card.type === "FREE_BALANCE" ? response_limit.data.availableValueVirtualCard : card.limitValue));
      setOriginalLimits(sortedResponse?.map((card: any) => card.limitValue));
      if((user?.roles?.includes("B_SELLER_ADMIN") || user?.roles?.includes("BANKING_ADMIN") || user?.roles?.includes("BANKING_CARD"))) {
        const response_invoice = await getCreditCardInvoicesList();
        const { dueDate, endDate, status } = getPriorityInvoiceDates(response_invoice.data);
        const formattedObject = {
          endDate,
          dueDate,
          status
        }
        setCurrentCreditCardInvoiceDate(formattedObject);
      } else {
        setCurrentCreditCardInvoiceDate({endDate: null, dueDate: null, status: "OPEN"})
      }
    } catch(error)  {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setInitialDataIsLoading(false);
    }
  }

  async function getInitialCreditCardInvoicesListData() {
    try {
      setInitialDataIsLoading(true);
      const response = await getCreditCardInvoicesList();
      setCreditCardsInvoices(response?.data);
    } catch(error)  {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setInitialDataIsLoading(false);
    }
  }

  async function getInitialCreditCardInvoicesData(month: string) {
    try {
      setInitialDataIsLoading(true);
      const response = await getCreditCardInvoicesList(month);
      setCreditCardInvoice(response?.data);
    } catch(error)  {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setInitialDataIsLoading(false);
    }
  }

  async function getInitialCreditCardData(cardId: string) {
    try {
      setCreditCardLoading(true);
      const response = await getCreditCardId(cardId);
      setCreditCardData(response?.data);
      setSelectedCreditCardName(response?.data?.cardName);
      const response_transactions = await getCreditCardTransactions(cardId);
      const formattedResponse = response_transactions?.data
        ?.map((transaction) => ({
          ...transaction,
        }))
        ?.sort(
          (a, b) =>
            new Date(b.transactionDate).getTime() -
            new Date(a.transactionDate).getTime()
        );

      setCreditCardTransactionData(formattedResponse);
      if(Object.keys(currentCreditCardInvoiceDate).length === 0) {
        const response_invoice = await getCreditCardInvoicesList();
        const { dueDate, endDate, status} = getPriorityInvoiceDates(response_invoice.data);
        const formattedObject = {
          endDate,
          dueDate,
          status,
        }
        setCurrentCreditCardInvoiceDate(formattedObject);
      }
    } catch(error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setCreditCardLoading(false);
    }
  }

  return (
    <CreditCardContext.Provider
      value={{
        createCreditCard,
        creditCards,
        loading,
        setLoading,
        getCreditCards,
        getInitialData,
        getInitialCreditCardData,
        setSelectedCreditCardName,
        selectedCreditCardName,
        creditCardData,
        creditCardLoading,
        setCreditCardLoading,
        creditCardTransactionsData,
        initialDataIsLoading, 
        creditCardLimit,
        updateCardStatus,
        getTotalValueTransactions,
        setCreditCardData,
        deleteCreditCard,
        renameCreditCard,
        changeCreditCardLimit,
        editedLimits,
        originalLimits,
        setEditedLimits,
        downloadCreditCardStatement,
        getInitialCreditCardInvoicesListData,
        creditCardsInvoices,
        patchDueDateCreditCardInvoice,
        creditCardInvoice,
        getInitialCreditCardInvoicesData,
        getInvoicesReady,
        getInvoicesPendingPayment,
        postGeneratePayment,
        currentCreditCardInvoiceDate
      }}
    >
      {children}
    </CreditCardContext.Provider>
  );
};

export function useCreditCard(): CreditCardContextData {
  return useContext(CreditCardContext);
}

export { CreditCardProvider };
